import { useCallback, useMemo, useState } from 'react';
import { buildDateRangeFilters } from '@/ui/composites/grid/gridFilters';

type UseDetailDateRangeStateParams = {
  isAllMode: boolean;
  rangeStartIso: string | null;
  rangeEndIso: string | null;
  allModeDefaultStartDay: string | null;
  allModeDefaultEndDay: string | null;
};

type UseDetailDateRangeStateResult = {
  selectedStartDay: string | null;
  setSelectedStartDay: (value: string | null) => void;
  selectedEndDay: string | null;
  setSelectedEndDay: (value: string | null) => void;
  hasCustomDates: boolean;
  pickerStartDay: string | null;
  pickerEndDay: string | null;
  handleStartDayChange: (nextDay: string | null) => void;
  handleEndDayChange: (nextDay: string | null) => void;
  clearSelectedDays: () => void;
};

export const buildSelectedDayRangeFilters = (
  field: string,
  selectedStartDay: string | null,
  selectedEndDay: string | null,
): ReturnType<typeof buildDateRangeFilters> => {
  if (!selectedStartDay && !selectedEndDay) return [];
  const start = selectedStartDay ? `${selectedStartDay}T00:00:00.000Z` : null;
  const end = selectedEndDay ? `${selectedEndDay}T23:59:59.999Z` : null;
  return buildDateRangeFilters(field, start, end);
};

export const resolveChartDaySelection = (
  selectedStartDay: string | null,
  selectedEndDay: string | null,
  day: string | null | undefined,
): { startDay: string | null; endDay: string | null } => {
  if (!day) {
    return { startDay: selectedStartDay, endDay: selectedEndDay };
  }
  const isSameDay = selectedStartDay === day && selectedEndDay === day;
  if (isSameDay) {
    return { startDay: null, endDay: null };
  }
  return { startDay: day, endDay: day };
};

export const useDetailDateRangeState = ({
  isAllMode,
  rangeStartIso,
  rangeEndIso,
  allModeDefaultStartDay,
  allModeDefaultEndDay,
}: UseDetailDateRangeStateParams): UseDetailDateRangeStateResult => {
  const [selectedStartDay, setSelectedStartDay] = useState<string | null>(null);
  const [selectedEndDay, setSelectedEndDay] = useState<string | null>(null);
  const hasCustomDates = Boolean(selectedStartDay ?? selectedEndDay);

  const pickerStartDay = useMemo(() => {
    if (selectedStartDay) return selectedStartDay;
    if (!isAllMode) {
      return rangeStartIso ? rangeStartIso.slice(0, 10) : null;
    }
    return allModeDefaultStartDay;
  }, [allModeDefaultStartDay, isAllMode, rangeStartIso, selectedStartDay]);

  const pickerEndDay = useMemo(() => {
    if (selectedEndDay) return selectedEndDay;
    if (!isAllMode) {
      return rangeEndIso ? rangeEndIso.slice(0, 10) : null;
    }
    return allModeDefaultEndDay;
  }, [allModeDefaultEndDay, isAllMode, rangeEndIso, selectedEndDay]);

  const handleStartDayChange = useCallback(
    (nextDay: string | null) => {
      setSelectedStartDay(nextDay);
      if (!nextDay) {
        setSelectedEndDay(null);
        return;
      }
      if (!selectedEndDay) {
        setSelectedEndDay(pickerEndDay ?? nextDay);
      }
    },
    [pickerEndDay, selectedEndDay],
  );

  const handleEndDayChange = useCallback(
    (nextDay: string | null) => {
      setSelectedEndDay(nextDay);
      if (!nextDay) {
        setSelectedStartDay(null);
        return;
      }
      if (!selectedStartDay) {
        setSelectedStartDay(pickerStartDay ?? nextDay);
      }
    },
    [pickerStartDay, selectedStartDay],
  );

  const clearSelectedDays = useCallback(() => {
    setSelectedStartDay(null);
    setSelectedEndDay(null);
  }, []);

  return {
    selectedStartDay,
    setSelectedStartDay,
    selectedEndDay,
    setSelectedEndDay,
    hasCustomDates,
    pickerStartDay,
    pickerEndDay,
    handleStartDayChange,
    handleEndDayChange,
    clearSelectedDays,
  };
};
